/**
 * File:        resources/js/components/ui/button.tsx
 * Module:      Design System · Primitives
 * Purpose:     shadcn Button primitive retuned to the DialN brand tokens — brand-tinted
 *              default/ghost/link, refined sizing, and a `loading` slot that shows an
 *              inline spinner while preserving width.
 *
 * Exports:
 *   - Button(buttonProps)            — the Button component
 *   - buttonVariants                 — cva variant factory (variant × size)
 *   - ButtonProps                    — `{ variant?, size?, asChild?, loading? } & ButtonHTMLAttributes`
 *
 * Variant matrix:
 *   - default    → bg-brand-600 hover:bg-brand-700 text-white
 *   - destructive → red, destructive tokens
 *   - outline    → bordered, ghost on hover
 *   - secondary  → neutral-tinted
 *   - ghost      → transparent; hover:bg-brand-50 hover:text-brand-700
 *   - link       → text-brand-600 underlined
 *
 * Size matrix (per brand spec §4.2):
 *   - sm        → h-8 px-3
 *   - default   → h-9 px-4
 *   - lg        → h-10 px-6
 *   - icon      → size-9 (square)
 *   - icon-sm   → size-8
 *   - icon-lg   → size-10
 *
 * Side-effects:
 *   - none
 *
 * Key invariants:
 *   - When `loading` is true, the Button is disabled and renders an inline spinner next to
 *     children to preserve button width.
 *   - `asChild` uses Radix Slot so callers may render as <Link>/<a>.
 *
 * Author:      Aman Vats Sharma
 * Last-updated: 2026-06-17
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500/40 dark:bg-brand-500 dark:hover:bg-brand-600",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-950 dark:hover:text-brand-300",
        link: "text-brand-600 underline-offset-4 hover:underline dark:text-brand-400",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * When true, the button renders an inline spinner before its children and
   * is forced into the disabled state to prevent re-submission. Width is
   * preserved by wrapping children and spinner in the same inline-flex span.
   */
  loading?: boolean
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"
  const isLoading = loading

  return (
    <Comp
      data-slot="button"
      data-loading={isLoading || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading ? (
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
          <span>{children}</span>
        </span>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }